export type HealthSubjectType = 'self' | 'partner' | 'child' | 'dependant' | 'pet';

export type HealthSubjectRecord = {
  subjectType?: string | null;
  subjectId?: string | null;
  subjectName?: string | null;
};

export type HealthSubject = {
  key: string;
  subjectType: HealthSubjectType;
  subjectId: string | null;
  subjectName: string;
  displayName: string;
  subtitle: string;
  iconHint: string;
  isFallback?: boolean;
};

type NamedSubject = {
  id: string | null;
  name: string | null;
};

type PetSubject = NamedSubject & {
  petType?: string | null;
};

export const HEALTH_PET_TYPE_OPTIONS = [
  'dog',
  'cat',
  'rabbit',
  'guinea pig',
  'hamster',
  'bird',
  'reptile',
  'fish',
  'horse',
  'other',
] as const;

export function formatPetTypeLabel(value: string | null | undefined) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return 'Pet';
  return trimmed
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function buildHealthSubjectKey(subjectType: HealthSubjectType, subjectId: string | null) {
  if (subjectType === 'self') return 'self';
  if (subjectType === 'partner') return 'partner';
  return `${subjectType}:${subjectId || 'saved'}`;
}

export function normalizeHealthSubjectRecord(record?: HealthSubjectRecord | null) {
  const rawType = String(record?.subjectType || '').trim();
  const subjectType: HealthSubjectType =
    rawType === 'partner' || rawType === 'child' || rawType === 'dependant' || rawType === 'pet'
      ? rawType
      : 'self';
  const subjectId = subjectType === 'self' || subjectType === 'partner'
    ? null
    : String(record?.subjectId || '').trim() || null;
  const subjectName = String(record?.subjectName || '').trim()
    || (subjectType === 'self'
      ? 'Me'
      : subjectType === 'partner'
        ? 'Partner'
        : subjectType === 'child'
          ? 'Child'
          : subjectType === 'dependant'
            ? 'Adult dependant'
            : 'Pet');

  return {
    subjectType,
    subjectId,
    subjectName,
    key: buildHealthSubjectKey(subjectType, subjectId),
  };
}

function buildSelfSubject(): HealthSubject {
  return {
    key: 'self',
    subjectType: 'self',
    subjectId: null,
    subjectName: 'Me',
    displayName: 'Me',
    subtitle: 'Your reminders and medications',
    iconHint: '🙂',
  };
}

function buildPartnerSubject(name: string): HealthSubject {
  return {
    key: 'partner',
    subjectType: 'partner',
    subjectId: null,
    subjectName: name,
    displayName: name,
    subtitle: 'Partner',
    iconHint: '❤️',
  };
}

function buildChildSubject(child: NamedSubject): HealthSubject {
  const name = String(child.name || '').trim();
  return {
    key: buildHealthSubjectKey('child', child.id),
    subjectType: 'child',
    subjectId: child.id,
    subjectName: name,
    displayName: name,
    subtitle: 'Child',
    iconHint: '🧒',
  };
}

function buildDependantSubject(adult: NamedSubject): HealthSubject {
  const name = String(adult.name || '').trim();
  return {
    key: buildHealthSubjectKey('dependant', adult.id),
    subjectType: 'dependant',
    subjectId: adult.id,
    subjectName: name,
    displayName: name,
    subtitle: 'Adult dependant',
    iconHint: '🧑',
  };
}

function buildPetSubject(pet: PetSubject): HealthSubject {
  const name = String(pet.name || '').trim();
  return {
    key: buildHealthSubjectKey('pet', pet.id),
    subjectType: 'pet',
    subjectId: pet.id,
    subjectName: name,
    displayName: name,
    subtitle: formatPetTypeLabel(pet.petType),
    iconHint: '🐾',
  };
}

function buildFallbackSubject(record: ReturnType<typeof normalizeHealthSubjectRecord>): HealthSubject {
  const subtitle =
    record.subjectType === 'self'
      ? 'Your reminders and medications'
      : record.subjectType === 'partner'
        ? 'Partner'
        : record.subjectType === 'child'
          ? 'Child'
          : record.subjectType === 'dependant'
            ? 'Adult dependant'
            : 'Pet';

  const iconHint =
    record.subjectType === 'self'
      ? '🙂'
      : record.subjectType === 'partner'
        ? '❤️'
        : record.subjectType === 'child'
          ? '🧒'
          : record.subjectType === 'dependant'
            ? '🧑'
            : '🐾';

  return {
    key: record.key,
    subjectType: record.subjectType,
    subjectId: record.subjectId,
    subjectName: record.subjectName,
    displayName: record.subjectName,
    subtitle,
    iconHint,
    isFallback: true,
  };
}

export function buildHealthSubjects(input: {
  partnerName?: string | null;
  children?: NamedSubject[];
  adultDependants?: NamedSubject[];
  pets?: PetSubject[];
  records?: HealthSubjectRecord[];
}) {
  const subjects: HealthSubject[] = [buildSelfSubject()];
  const subjectKeys = new Set<string>(subjects.map((subject) => subject.key));

  const pushSubject = (subject: HealthSubject | null) => {
    if (!subject || subjectKeys.has(subject.key)) return;
    subjectKeys.add(subject.key);
    subjects.push(subject);
  };

  const partnerName = String(input.partnerName || '').trim();
  if (partnerName) {
    pushSubject(buildPartnerSubject(partnerName));
  }

  (input.children || [])
    .filter((child) => String(child.name || '').trim().length > 0)
    .forEach((child) => pushSubject(buildChildSubject({ id: child.id, name: child.name })));

  (input.adultDependants || [])
    .filter((adult) => String(adult.name || '').trim().length > 0)
    .forEach((adult) => pushSubject(buildDependantSubject({ id: adult.id, name: adult.name })));

  (input.pets || [])
    .filter((pet) => String(pet.name || '').trim().length > 0)
    .forEach((pet) => pushSubject(buildPetSubject(pet)));

  (input.records || [])
    .map((record) => normalizeHealthSubjectRecord(record))
    .sort((left, right) => left.subjectName.localeCompare(right.subjectName))
    .forEach((record) => {
      // Child and pet accordions should only exist when those profiles are actually saved.
      if (record.subjectType === 'child' || record.subjectType === 'pet') return;
      pushSubject(buildFallbackSubject(record));
    });

  return subjects;
}

export function recordBelongsToHealthSubject(
  subject: HealthSubject,
  record?: HealthSubjectRecord | null,
) {
  return normalizeHealthSubjectRecord(record).key === subject.key;
}
